import { useState } from 'react';
import { ApiService } from '../services/apiService';
import { ViewMode } from '../contexts/ViewModeContext';

interface CreateTrendCategoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  viewMode: ViewMode;
}

export const CreateTrendCategoryDialog = ({ isOpen, onClose, onSuccess, viewMode }: CreateTrendCategoryDialogProps) => {
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [fields, setFields] = useState({
    factsEmbedding: false,
    causeOfActionEmbedding: true,
    reliefEmbedding: false
  });
  const [threshold, setThreshold] = useState(0.7);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const apiService = ApiService.getInstance();
  const isAdvanced = viewMode === 'admin';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      setError('Topic or phrase is required');
      return;
    }

    if (isAdvanced && !name.trim()) {
      setError('Name is required');
      return;
    }

    const selectedFields = isAdvanced
      ? Object.entries(fields).filter(([, v]) => v).map(([k]) => k)
      : ['causeOfActionEmbedding'];

    if (selectedFields.length === 0) {
      setError('Select at least one field');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await apiService.createTrendCategory({
        name: isAdvanced ? name.trim() : query.trim(),
        query: query.trim(),
        fields: selectedFields,
        threshold: isAdvanced ? threshold : 0.70
      });
      onSuccess();
      handleClose();
    } catch {
      setError('Failed to create topic');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setQuery('');
    setFields({ factsEmbedding: false, causeOfActionEmbedding: true, reliefEmbedding: false });
    setThreshold(0.7);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div data-component="CreateTrendCategoryDialog" className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Trend Topic</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {isAdvanced && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g., Employment Discrimination"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isAdvanced ? 'Query' : 'Causes Topic or Phrase'}
            </label>
            <textarea
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="e.g., workplace discrimination based on race"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {isAdvanced && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fields</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={fields.factsEmbedding}
                    onChange={e => setFields(p => ({ ...p, factsEmbedding: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">Facts</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={fields.causeOfActionEmbedding}
                    onChange={e => setFields(p => ({ ...p, causeOfActionEmbedding: e.target.checked }))}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">Cause</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={fields.reliefEmbedding}
                    onChange={e => setFields(p => ({ ...p, reliefEmbedding: e.target.checked }))}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">Relief</span>
                </label>
              </div>
            </div>
          )}

          {isAdvanced && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Threshold: {threshold.toFixed(2)}
              </label>
              <input
                type="range"
                min="0.5"
                max="0.95"
                step="0.01"
                value={threshold}
                onChange={e => setThreshold(parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>0.50</span>
                <span>0.95</span>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
